// import { List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { IList } from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import styled from 'styled-components';
// import { LabelIcon } from '../../../constants/icon-definitions';
import { viewTexts } from '../../../constants/view-texts';
import { ILabelFromApiType } from '../../../types/record/label';
import { MuiButton, MuiSelect } from '../../shared/form-element';
import { AnnotationLables } from '../../shared/annotation-labels';
import { MessageArea } from '../../shared/message-area';
import { LoadingButtonState } from '../../shared/mui/loading-button-state';
import { ButtonWithLoadingCircle } from '../../shared/mui/mui-loading-button';
import { H3 } from '../../shared/styled/heading';
import { Root } from '../../shared/styled/root';
// import { hueToDefaultHex } from '../../../functions/hue-to-hex';

const vt = viewTexts.pages.modelDetail;

const MessageAreaWrapper = styled.div`
  padding: 20px 0;
`;

const Body = styled.div`
  padding: 10px;
`;

const Section = styled.div`
  padding: 20px 0;
`;

const Description = styled.div`
  padding: 5px;
`;

const AnnotationLabelsWrapper = styled.div`
  padding: 5px;
`;

const SelectorWrapper = styled.div`
  width: 300px;
`;

interface Props {
  labelList: IList<ILabelFromApiType>;
  modelsHistory: IList<{ id: number; label: string }>;
  selectedModelVersionId: number | undefined;
  onSelectedModelVersionIdChange: (id: number) => void;
  conditionalViewValues: {
    messages: { status: 'default' | 'alert'; messages: readonly string[] };
    descriptions: {
      modelImages: string[];
      executeTraining: string;
    };
    buttonsEnabled: {
      editLabels: boolean;
      editModelImages: boolean;
    };
  };
  onEditLabelClick: () => void;
  onEditModelImagesClick: () => void;
  startTrainingButtonLabel: string;
  startTrainingButtonIsDisabled: boolean;
  startTrainingButtonStatus: LoadingButtonState['status'];
  onStartTrainingClick: () => void;
}

export const ModelDetailView: FC<Props> = memo(
  ({
    labelList,
    modelsHistory,
    selectedModelVersionId,
    conditionalViewValues,
    onEditLabelClick,
    onEditModelImagesClick,
    onSelectedModelVersionIdChange,
    startTrainingButtonLabel,
    startTrainingButtonIsDisabled,
    startTrainingButtonStatus,
    onStartTrainingClick
  }: Readonly<Props>) => {
    const selectedModelVersionIdStr =
      selectedModelVersionId === undefined
        ? undefined
        : selectedModelVersionId.toString();

    const modelsHistoryWithStringId = useMemo<{ id: string; label: string }[]>(
      () =>
        modelsHistory
          .map(({ id, label }) => ({ id: id.toString(), label }))
          .toArray(),
      [modelsHistory]
    );

    const onSelectedModelVersionStringIdChange = useCallback(
      (id: string | undefined) => {
        if (id === undefined) return;
        onSelectedModelVersionIdChange(Number(id));
      },
      [onSelectedModelVersionIdChange]
    );

    return (
      <Root>
        <Description>{vt.description}</Description>
        {conditionalViewValues.messages.messages.length > 0 ? (
          <MessageAreaWrapper>
            <MessageArea
              status={conditionalViewValues.messages.status}
              messages={conditionalViewValues.messages.messages}
            />
          </MessageAreaWrapper>
        ) : (
          undefined
        )}
        <Body>
          <Section>
            <H3>{vt.section1.title}</H3>
              {labelList.isEmpty() ? (
                <div>{vt.section1.labelIsUndefined}</div>
              ) : (
                <AnnotationLabelsWrapper>
                  <AnnotationLables labelList={labelList} />
                </AnnotationLabelsWrapper>
              )}
            <MuiButton
              label={vt.section1.goToLabelList}
              color='primary'
              onClick={onEditLabelClick}
              disabled={!conditionalViewValues.buttonsEnabled.editLabels}
            />
          </Section>
          <hr />
          <Section>
            <H3>{vt.section2.title}</H3>
            <Description>
              {conditionalViewValues.descriptions.modelImages.map((s, idx) => (
                <div key={idx}>{s}</div>
              ))}
            </Description>
            <MuiButton
              label={vt.section2.goToModelImageList}
              color='primary'
              onClick={onEditModelImagesClick}
              disabled={!conditionalViewValues.buttonsEnabled.editModelImages}
            />
          </Section>
          <hr />

          <Section>
            <div>
              <H3>{vt.section3.common.title}</H3>
              <Description>
                {conditionalViewValues.descriptions.executeTraining}
              </Description>
              <ButtonWithLoadingCircle
                label={startTrainingButtonLabel}
                status={startTrainingButtonStatus}
                disabled={startTrainingButtonIsDisabled}
                onClick={onStartTrainingClick}
              />
            </div>

            {modelsHistory.isEmpty() ? (
              undefined
            ) : (
              <div>
                <H3>{vt.section3.version.title}</H3>
                <Description>{vt.section3.version.description}</Description>
                <SelectorWrapper>
                  <MuiSelect
                    options={modelsHistoryWithStringId}
                    selectedId={selectedModelVersionIdStr}
                    onChange={onSelectedModelVersionStringIdChange}
                    useEmptyLabel={false}
                    fullWidth={true}
                  />
                </SelectorWrapper>
              </div>
            )}
          </Section>
        </Body>
      </Root>
    );
  }
);

ModelDetailView.displayName = 'ModelDetailView';
